import { useEffect, useRef, useState } from 'react';
import { Pencil, Check, X, RotateCcw } from 'lucide-react';

type Variant = 'body' | 'title' | 'mono';

interface Props {
  /** Current effective value (override ?? original). */
  value: string;
  /** Original AI-generated value — used for the "revert" comparison. */
  original?: string;
  /** Whether an override is currently active. Drives the "Edited · revert" chip. */
  overridden?: boolean;
  onSave: (next: string) => void | Promise<void>;
  onRevert?: () => void;
  readOnly?: boolean;
  multiline?: boolean;
  placeholder?: string;
  variant?: Variant;
  /** Wrapper classes (hover/focus targets, layout). */
  className?: string;
  /** Classes applied only to the value text (not the meta chip). */
  valueClassName?: string;
  /** Short byline on the "edited" chip (e.g. "Nim · just now"). */
  editMeta?: string;
}

/**
 * Click-to-edit primitive for the C preview. Mirrors PR #196's EditableText API
 * (value / onSave / readOnly / multiline) but styled to the C system: warm
 * paper-warm focus bg, DM Sans input, brand-ink focus ring, no amber.
 *
 * Drop-in replacement for the production `EditableText` once the preview ships.
 */
export function InlineEdit({
  value,
  original,
  overridden,
  onSave,
  onRevert,
  readOnly = false,
  multiline = false,
  placeholder = 'Click to edit',
  variant = 'body',
  className = '',
  valueClassName = '',
  editMeta,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Select all text on entry so the user can just start typing
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      } else {
        (inputRef.current as HTMLTextAreaElement).select();
      }
    }
  }, [editing]);

  const commit = async () => {
    if (saving) return;
    const next = draft.trim();
    if (next === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const variantTextClass =
    variant === 'title'
      ? 'font-display text-lg font-semibold leading-tight'
      : variant === 'mono'
        ? 'font-mono text-sm'
        : 'text-sm leading-relaxed';

  const variantInputClass =
    variant === 'title'
      ? 'font-display text-lg font-semibold leading-tight'
      : variant === 'mono'
        ? 'font-mono text-sm'
        : 'text-sm leading-relaxed';

  if (readOnly) {
    return (
      <div className={className}>
        <span className={`${variantTextClass} ${valueClassName}`}>
          {value || <span className="text-brand-text-3 italic">{placeholder}</span>}
        </span>
      </div>
    );
  }

  if (editing) {
    const sharedInputProps = {
      value: draft,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setDraft(e.target.value),
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !multiline) {
          e.preventDefault();
          commit();
        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      },
      onBlur: commit,
      disabled: saving,
      placeholder,
      className: `w-full bg-brand-paper-warm rounded-lg px-2.5 py-1.5 border border-brand-ink/15 outline-none ring-0 focus:border-brand-ink/30 focus:ring-2 focus:ring-brand-ink/10 font-brand text-brand-ink ${variantInputClass}`,
    };

    return (
      <div className={className}>
        {multiline ? (
          <textarea
            {...sharedInputProps}
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            rows={4}
          />
        ) : (
          <input
            {...sharedInputProps}
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
          />
        )}
        <div className="flex items-center gap-2 mt-1.5 font-meta text-[11px] text-brand-text-3">
          <span className="inline-flex items-center gap-1">
            <kbd className="bg-white border border-black/10 rounded px-1 py-0.5 text-[10px]">
              {multiline ? '⌘↵' : '↵'}
            </kbd>
            save
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="bg-white border border-black/10 rounded px-1 py-0.5 text-[10px]">esc</kbd>
            cancel
          </span>
          {saving && <span className="ml-auto text-brand-text-2">Saving...</span>}
        </div>
      </div>
    );
  }

  return (
    <div className={`group ${className}`}>
      <span
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setEditing(true);
          }
        }}
        className={`inline cursor-text border-b border-transparent hover:border-brand-ink/15 hover:bg-brand-paper-warm/60 rounded-sm transition-colors ${variantTextClass} ${valueClassName} ${value ? 'text-brand-ink' : 'text-brand-text-3 italic'}`}
      >
        {value || placeholder}
      </span>
      <button
        onClick={() => setEditing(true)}
        aria-label="Edit"
        className="ml-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity inline-flex items-center justify-center w-5 h-5 rounded hover:bg-brand-paper text-brand-text-3 hover:text-brand-ink align-middle"
      >
        <Pencil className="w-3 h-3" />
      </button>

      {overridden && (
        <div className="flex items-center gap-2 mt-1.5">
          <span className="inline-flex items-center gap-1 bg-brand-paper text-brand-text-3 px-1.5 py-0.5 rounded-full font-meta text-[10px] leading-none whitespace-nowrap">
            <Check className="w-2.5 h-2.5 text-brand-info shrink-0" />
            Edited by {editMeta ?? 'you'}
          </span>
          {onRevert && (
            <button
              onClick={onRevert}
              className="inline-flex items-center gap-1 text-brand-accent hover:underline font-meta text-[10px] leading-none whitespace-nowrap"
              title={original ? `Revert to: "${original.slice(0, 80)}${original.length > 80 ? '...' : ''}"` : 'Revert'}
            >
              <RotateCcw className="w-2.5 h-2.5 shrink-0" />
              Revert
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Helper hook: reads the demo role from localStorage. Returns `canEdit` true
 * for listing_agent / co_listing_agent, false otherwise.
 *
 * TODO(backend): replace with a real user-role lookup on the property record.
 */
export function useCanEdit(): boolean {
  const [role, setRole] = useState<string>(() => {
    if (typeof window === 'undefined') return 'listing_agent';
    return window.localStorage.getItem('discloser.demo.role') || 'listing_agent';
  });

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'discloser.demo.role') {
        setRole(e.newValue || 'listing_agent');
      }
    };
    window.addEventListener('storage', onStorage);
    // Also re-check on focus in case localStorage was updated in the same tab
    const onFocus = () =>
      setRole(window.localStorage.getItem('discloser.demo.role') || 'listing_agent');
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return role === 'listing_agent' || role === 'co_listing_agent';
}
